import * as React from 'react';

import * as XmlJS from 'xml-js';
import Exercise from '../../../modules/lessons/domain/aggregates/exercise/Exercise';
import ChoiceExerciseComponent from './ChoiceExerciseComponent';
import SetComponent from '../shared/SetComponent';
import ExplanationComponent from './ExplanationComponent';
import MonoComponent from '../shared/MonoComponent';
import ExerciseResultApi from '../../../modules/lessons/api/ExerciseResultApi';
import Registry from '../../../global/Registry';
import Page from '../../Page';
import BooleanExerciseComponent from './BooleanExerciseComponent';
import ChoicesComponent from './ChoicesComponent';
import TextExerciseComponent from './TextExerciseComponent';
import MathComponent from '../shared/MathComponent';
import NodeToTextConverter from '../../../modules/parsing/NodeToTextConverter';
import SyntacticAnalyzer from '../../../modules/parsing/SyntacticAnalyzer';
import LexicalAnalyzer from '../../../modules/parsing/LexicalAnalyzer';
import Node from '../../../modules/parsing/nodes/Node';
import MathBoxComponent from '../shared/MathBoxComponent';
import LinesComponent from '../lesson/WebGL/LinesComponent';
import StringMap from '../../../../../../../backend/code/foundation/dataStructures/maps/StringMap';
import AnalysisMode from '../../../modules/parsing/AnalysisMode';
import WaitingTimeCalculator from '../../../modules/lessons/domain/services/WaitingTimeCalculator';
import {Link} from 'react-router-dom';
import UserApi from '../../../modules/users/api/UserApi';
import { Button } from 'semantic-ui-react';
import PlainTextExerciseComponent from './PlainTextExerciseComponent';
import ExerciseTemplate from '../../../modules/lessons/domain/aggregates/exerciseTemplate/ExerciseTemplate';
import EntityId from '../../../../foundation/domain/EntityId';
import * as Voca from 'voca';

interface Props {
	exercise?: Exercise;
	onUpdate?: () => void;
	showAnswers?: boolean;
	onContinue?: () => void;
	content?: string;
	editingMode: boolean;
	continueLink?: string;
	step?: number;
}

interface State {
	results: Result[];
	complete: boolean;
	randomValues: StringMap<string> ;
}

class Result {
	private isCaseSensitive: boolean = false;
	private templateId: EntityId<ExerciseTemplate> = new EntityId<ExerciseTemplate>(null);

	public constructor (private correctValue: string|Node|string[], private value?: string|Node) {}

	public caseSensitive (caseSensitive: boolean) {
		this.isCaseSensitive = caseSensitive;
	}

	public update (value: string|Node) {
		this.value = value;
	}

	public getValue () {
		return this.value;
	}

	public hasValue () {
		return this.value !== undefined;
	}

	public getCorrectValue () {
		return this.correctValue;
	}

	public setTemplateId (templateId: EntityId<ExerciseTemplate>) {
		this.templateId = templateId;
	}

	public normalizedValue (value: string) {
		if (this.templateId.getValue() === 13) {
			value = Voca.replaceAll(value, 'I', 'ı');
			value = Voca.replaceAll(value, 'İ', 'i');
		}

		return value.toLocaleLowerCase().replace(/[.,?!;:`'"]/g, '').replace(/\s{2,}/g, ' ');
	}

	public isCorrect () {
		const value = this.value instanceof Node ? NodeToTextConverter.execute((this.value as Node).normalized()) : this.value;
		const correctValue = this.correctValue instanceof Node ? NodeToTextConverter.execute((this.correctValue as Node).normalized()) : this.correctValue;

		// Plain text exercises can have multiple correct answers and is therefore an array, we ignore interpunction and case for template 10
		return Array.isArray(correctValue) ? correctValue.map(value => (this.isCaseSensitive ? value : this.normalizedValue(value))).includes((this.isCaseSensitive ? (value as string) : this.normalizedValue(value as string))) : value === correctValue;
	}
}

export default class ExerciseComponent extends React.Component<Props, State> {

	private index: number;
	private firstParse = true;
	private isCompleted = false;
	private continueButton: Button;

	public constructor () {
		super();

		this.state = { results: [], complete: false, randomValues: {} };
	}

	public componentWillReceiveProps (props: Props) {
		if (props.exercise === undefined || this.props.exercise === undefined || props.step !== this.props.step || props.content !== this.props.content || props.editingMode) {
			this.state = { results: [], complete: false, randomValues: {} };
			this.firstParse = true;
			this.isCompleted = false;
		}
	}

	public updateResult = async (result: Result, value: string|Node) => {
		result.update(value);

		const complete = this.state.results.every(result => result.hasValue());

		if (complete && !this.state.complete && this.props.onUpdate) {
			// Prevent calling the api if we have already done so. This would otherwise happen because of switching back and forth between an answer and the question mark
			if (!this.isCompleted) {
				await ExerciseResultApi.getInstance().createExerciseResult(Registry.getInstance().me.getId(), (this.props.exercise as Exercise).getId(), this.getValue(), this.isCorrect());

				this.isCompleted = true;
			}

			this.props.onUpdate();
		}

		await this.setState({ complete: complete });

		if (this.isCompleted) {
			this.continueButton.focus();
		}
	}

	renderExerciseContents () {
		const { continueLink, editingMode, exercise, onContinue } = this.props;
		const me = Registry.getInstance().me;

		if (exercise === undefined) {
			return (
				<div style={{marginTop: '160px'}}>
					<p>
						You have finished all exercises.<br/>
						<small style={{fontSize: '0.6em', color: 'lightgrey'}}>In a while you will be able to repeat the exercises.</small><br/>
						{Registry.getInstance().me.isSignedUp() ? null : <small style={{fontSize: '0.6em', color: 'lightgrey'}}><Link to={'/sign-up'}>Sign up</Link> if you want to keep your progress.</small>}
					</p>
					{Registry.getInstance().me.isSignedUp() && !Registry.getInstance().me.getPreferences().reviewRemindersIsSet() ?
						<div>
							<p style={{fontSize: '0.8em'}}>Would you like us to remind you by email when it's time to review your exercises?</p>
							<Button color='red' href={continueLink} inverted style={{marginRight: '24px'}} onClick={ async (event: React.SyntheticEvent<HTMLButtonElement>) => { await UserApi.getInstance().changePreference('REVIEW_REMINDERS', false); Page.navigate(continueLink as string); event.preventDefault(); }}>No</Button>
							<Button color='green' href={continueLink} inverted onClick={async (event: React.SyntheticEvent<HTMLButtonElement>) => { await UserApi.getInstance().changePreference('REVIEW_REMINDERS', true); Page.navigate(continueLink as string); event.preventDefault(); }}>Yes</Button>
						</div>
						:
						<Button href={continueLink} inverted onClick={(event: React.SyntheticEvent<HTMLButtonElement>) => { Page.navigate(continueLink as string); event.preventDefault(); }}>Continue</Button>
					}
				</div>
			);
		}

		try {
			let waitingTime = this.isCorrect() ? '24 hours' : '30 seconds';

			if (this.state.complete && !editingMode) {
				const exerciseResult = Registry.getInstance().exerciseResultRepository.getLastByExerciseId(exercise.getId());
				const nextTimepoint = exerciseResult.getNextTimepoint();
				waitingTime = WaitingTimeCalculator.getInstance().execute(nextTimepoint);
			}

			return (
				<div style={{height: '100%', padding: '64px 0'}}>
					{/*<p style={{marginTop: '32px', visibility: this.state.complete ? 'visible' : 'hidden'}}>{this.isCorrect() ? 'Correct' : 'Incorrect'}</p>*/}
					{this.parse(XmlJS.xml2js('<root>' + this.computeExerciseContent(exercise) + '</root>').elements[0], {key: String(this.props.step)})}

					<div style={{visibility: this.state.complete ? 'visible' : 'hidden'}}>
						{this.state.complete ? <div style={{marginTop: '8px', fontSize: '0.7em'}}>Repetition in {waitingTime}</div> : null}

						<Button ref={button => this.continueButton = button} inverted style={{marginTop: '32px'}} onClick={onContinue}>Continue</Button>

						{this.props.editingMode ? null : <div style={{margin: '48px 0 16px 0', fontSize: '0.5em'}}><Link to={Registry.getInstance().lessonRepository.getById(exercise.getLessonId()).getSlug()}>Review lesson</Link></div>}
						{this.props.editingMode || !me.isErik() ? null : <div style={{margin: '48px 0 16px 0', fontSize: '0.5em'}}><Link to={exercise.getEditingSlug()}>Edit exercise</Link></div>}
					</div>

				</div>
			);

		} catch (error) {
			return error.message;
		}
	}

	render () {
		const result = (
			<div className='exercise-inner'>
				{this.renderExerciseContents()}
			</div>
		);

		this.firstParse = false;

		return result;
	}

	private rerender () {
		this.forceUpdate();
	}

	private isCorrect () {
		return this.state.results.every(result => result.isCorrect());
	}

	private getValue () {
		return JSON.stringify(this.state.results.map(result => result.getValue()));
	}

	private computeExerciseContent (exercise: Exercise) {
		if (exercise.getTemplateId().isNull() || !exercise.hasContentAtIndex(0)) {
			return exercise.getContent();
		}

		const exerciseTemplate = Registry.getInstance().exerciseTemplateRepository.getById(exercise.getTemplateId());

		let remainingTemplateContent = exerciseTemplate.getContent();
		let content = '';

		for (let i = 0; i < exercise.getContents().length; i++) {
			const index = remainingTemplateContent.indexOf('{{');
			const match = remainingTemplateContent.match(/{{[^:]+:([^}]+)}}/);

			if (match === null) {
				throw new Error('No match for exercise content');
			}

			content += remainingTemplateContent.substr(0, index);
			content += this.computeContentValue(match[1], exercise.getContents()[i]);

			remainingTemplateContent = remainingTemplateContent.substr(index + match[0].length);
		}

		content += remainingTemplateContent;

		// Replace random values
		return content.replace(/\*value\*/g, '<Value name="value" />');
	}

	private computeContentValue(type: string, rawValue: string) {
		return type === 'choices' ? rawValue.split('\n').map(value => '<Choice>' + value + '</Choice>').join('') : type === 'values' ? rawValue.split('\n').map(value => '<Value>' + value + '</Value>').join('') : rawValue;
	}

	private parse (value: any, context: StringMap<string>): any {
		const firstParse = this.firstParse;
		this.index = 0;
		const key = context.key !== undefined ? Number(context.key) : undefined;
		const { parent } = context;

		if (Array.isArray(value)) {
			return value.length === 1 ? this.parse(value[0], context) : value.map((elementValue, key) => this.parse(elementValue, Object.assign(context, {key})));
		}

		if (typeof value === 'object' && value.type === 'text') {
			return <span key={key}>{value.text}</span>;
		}

		if (typeof value === 'object' && value.type === 'element') {

			if (value.name === 'root') {
				return <div key={key}>{this.parse(value.elements, context)}</div>;
			}

			if (value.name === 'BooleanExercise') {
				let result: Result;

				if (firstParse) {
					result = new Result(value.attributes.answer);
					this.state.results.push(result);
				} else {
					result = this.state.results[this.index++];
				}

				return <BooleanExerciseComponent complete={this.state.complete || this.props.showAnswers === true} correct={this.props.showAnswers ? true : result.isCorrect()} value={this.props.showAnswers ? result.getCorrectValue() as string : result.getValue() as string} onChange={(value: string) => this.updateResult(result, value)} key={key}>{this.parse(value.elements, context)}</BooleanExerciseComponent>;
			}

			if (value.name === 'ChoiceExercise') {
				return <ChoiceExerciseComponent key={key}>{this.parse(value.elements, context)}</ChoiceExerciseComponent>;
			}

			if (value.name === 'RandomValue') {
				if (firstParse) {
					const values = value.elements.map((value: any, key: number) => this.parse(value, Object.assign(context, {key})));
					const randomIndex = Math.floor(Math.random() * values.length);
					// const randomIndex = IntegerHandler.createRandom(values.length);

					this.state.randomValues[value.attributes.name] = values[randomIndex];

					if (value.attributes.indexName !== undefined) {
						this.state.randomValues[value.attributes.indexName] = String(randomIndex);
					}
				}

				return '';
			}

			if (value.name === 'Value') {
				if (value.attributes !== undefined && value.attributes.name !== undefined) {
					return this.state.randomValues[value.attributes.name];
				}

				return value.elements[0].text;
			}

			if (value.name === 'Choices') {
				const values: string[] = value.elements[0].name === 'Choice' ? this.parse(value.elements, context).map((element: any) => String(element.text.props.children)) : JSON.parse(value.elements[0].text);

				let result: Result;

				if (firstParse) {
					result = new Result(values[0]);
					this.state.results.push(result);
				} else {
					result = this.state.results[this.index++];
				}

				const choices: { text: string; value: string|undefined; }[] = values.sort().map(value => { return {text: value, value}; });
				choices.unshift({text: '?', value: undefined});

				return <ChoicesComponent rerender={() => this.rerender()} complete={this.state.complete} color={this.props.showAnswers === true ? 'green' : this.state.complete ? (result.isCorrect() ? 'green' : 'red') : 'inherit'} value={this.props.showAnswers ? result.getCorrectValue() as string : result.getValue() as string} onChange={(choiceValue: string) => this.updateResult(result, choiceValue)} key={(this.props.exercise ? this.props.exercise.getId().getValue() : '') + '_' + String(key)} choices={choices} />;
			}

			if (value.name === 'ComplexChoices') {
				const choices = value.elements[0].name === 'Choice' ? this.parse(value.elements, context) : value.elements.map((element: any, index: number) => { return {value: String(index), text: this.parse(element, context)}; });

				choices.unshift({text: '?', value: undefined});

				let result: Result;

				if (firstParse) {
					let answer = value.attributes.answer;

					if (!/\d/.test(answer)) {
						answer = this.state.randomValues[answer];
					}

					result = new Result(answer);
					this.state.results.push(result);
				} else {
					result = this.state.results[this.index++];
				}

				return <ChoicesComponent rerender={() => this.rerender()} complete={this.state.complete} color={this.props.showAnswers === true ? 'green' : this.state.complete ? (result.isCorrect() ? 'green' : 'red') : 'inherit'} value={this.props.showAnswers ? result.getCorrectValue() as string : result.getValue() as string} onChange={(choiceValue: string) => this.updateResult(result, choiceValue)} key={(this.props.exercise ? this.props.exercise.getId().getValue() : '') + '_' + String(key)} choices={choices} />;
			}

			if (value.name === 'MultipleChoice') {
				let choices = this.parse(value.elements, context);

				if (!Array.isArray(choices)) {
					choices = [choices];
				}

				let result: Result;

				if (firstParse) {
					let answer = value.attributes.answer;

					if (!/\d/.test(answer)) {
						answer = this.state.randomValues[answer];
					}

					result = new Result(answer);
					this.state.results.push(result);
				} else {
					result = this.state.results[this.index++];
				}

				return <div key={key} style={{margin: '24px'}}>
					{choices.map((choice: any, index: number) => {
						const button = <Button style={{margin: '0 24px'}} inverted key={index} onClick={() => this.updateResult(result, choice.value)} color={this.props.showAnswers === true && index === Number(result.getValue()) ? 'green' : this.state.complete && index === Number(result.getValue()) ? (result.isCorrect() ? 'green' : 'red') : undefined}>{false && <span style={{ color: 'grey', marginRight: '12px' }}>{index + 1}</span>}{choice.text}</Button>;

						return choices.length === 2 ? button : <div key={index}>{button}</div>;
					})}
				</div>;
			}

			if (value.name === 'TextExercise') {
				let result: Result;

				const correctValue = SyntacticAnalyzer.execute(LexicalAnalyzer.execute(value.attributes.answer, AnalysisMode.TEXT), AnalysisMode.TEXT);

				if (firstParse) {
					result = new Result(correctValue);
					this.state.results.push(result);
				} else {
					result = this.state.results[this.index++];
				}

				return <TextExerciseComponent showAnswer={this.props.showAnswers === true} analysisMode={AnalysisMode.TEXT} editingMode={this.props.editingMode} key={(this.props.exercise ? this.props.exercise.getId().getValue() : '') + '_' + String(key)} correct={!result.hasValue() ? undefined : result.isCorrect()} correctAnswer={result.getCorrectValue() as Node} rerender={() => this.rerender()} onAnswer={(answer: Node) => this.updateResult(result, answer)} />;
			}

			if (value.name === 'PlainTextExercise') {
				let result: Result;

				const correctValue = value.attributes.answer.split('\n');

				if (firstParse) {
					result = new Result(correctValue);
					result.caseSensitive(value.attributes.caseSensitive === 'true');
					result.setTemplateId((this.props.exercise as Exercise).getTemplateId());
					this.state.results.push(result);
				} else {
					result = this.state.results[this.index++];
				}

				return <PlainTextExerciseComponent showAnswer={this.props.showAnswers === true} editingMode={this.props.editingMode} key={(this.props.exercise ? this.props.exercise.getId().getValue() : '') + '_' + String(key)} correct={!result.hasValue() ? undefined : result.isCorrect()} correctAnswer={result.getCorrectValue() as string[]} rerender={() => this.rerender()} onAnswer={(answer: string) => this.updateResult(result, answer)} />;
			}

			if (value.name === 'MathExercise') {
				let result: Result;

				if (firstParse) {
					result = new Result(SyntacticAnalyzer.execute(LexicalAnalyzer.execute(value.attributes.answer, AnalysisMode.MATH), AnalysisMode.MATH));
					this.state.results.push(result);
				} else {
					result = this.state.results[this.index++];
				}

				return <TextExerciseComponent showAnswer={this.props.showAnswers === true} analysisMode={AnalysisMode.MATH} editingMode={this.props.editingMode} key={(this.props.exercise ? this.props.exercise.getId().getValue() : '') + '_' + String(key)} correct={!result.hasValue() ? undefined : result.isCorrect()} correctAnswer={result.getCorrectValue() as Node} rerender={() => this.rerender()} onAnswer={(answer: Node) => this.updateResult(result, answer)} />;
			}

			if (value.name === 'Choice') {
				return {value: String(key), text: this.parse(value.elements, context)};
			}

			if (value.name === 'Explanation') {
				return <ExplanationComponent visible={this.state.complete || this.props.showAnswers === true} key={key}>{this.parse(value.elements, Object.assign(context, {key, parent: 'Explanation'}))}</ExplanationComponent>;
			}

			if (value.name === 'p') {
				return <p key={key}>{this.parse(value.elements, context)}</p>;
			}

			if (value.name === 'sub') {
				return <sub key={key}>{this.parse(value.elements, context)}</sub>;
			}

			if (value.name === 'sup') {
				return <sup key={key}>{this.parse(value.elements, context)}</sup>;
			}

			if (value.name === 'a') {
				return value.attributes.href[0] !== '/' ? <a key={key} href={value.attributes.href} target='_blank'>{this.parse(value.elements, context)}</a> : <Link to={value.attributes.href}>{this.parse(value.elements, context)}</Link>;
			}

			if (value.name === 'Mono') {
				return <MonoComponent key={key}>{this.parse(value.elements, context)}</MonoComponent>;
			}

			if (value.name === 'Set') {
				return <SetComponent key={key}>{this.parse(value.elements, context)}</SetComponent>;
			}

			if (value.name === 'strong') {
				return <strong key={key}>{this.parse(value.elements, context)}</strong>;
			}

			if (value.name === 'em') {
				return <em key={key}>{this.parse(value.elements, context)}</em>;
			}

			if (value.name === 'img') {
				let cutTop = 0, cutRight = 0, cutBottom = 0, cutLeft = 0;

				if (value.attributes.crop) {
					const cuts = (value.attributes.crop as string).split(' ').map(crop => Number(crop));
					if (cuts.length === 1) {
						cutTop = cutRight = cutBottom = cutLeft = cuts[0];
					} else if (cuts.length === 2) {
						cutTop = cutBottom = cuts[0];
						cutRight = cutLeft = cuts[1];
					} else if (cuts.length === 2) {
						cutTop = cuts[0];
						cutRight = cutLeft = cuts[1];
						cutBottom = cuts[2];
					} else if (cuts.length === 4) {
						cutTop = cuts[0];
						cutRight = cuts[1];
						cutBottom = cuts[2];
						cutLeft = cuts[3];
					}
				}

				let queryArray = [];
				if (cutTop !== 0) {
					queryArray.push('top=' + cutTop);
				}
				if (cutRight !== 0) {
					queryArray.push('right=' + cutRight);
				}
				if (cutBottom !== 0) {
					queryArray.push('bottom=' + cutBottom);
				}
				if (cutLeft !== 0) {
					queryArray.push('left=' + cutLeft);
				}

				let source = value.attributes.src;

				if (queryArray.length !== 0) {
					source = 'https://brainbrooder.com/api/image?url=' + encodeURIComponent(source) + '&' + queryArray.join('&');
				}

				return <img key={key} alt={value.attributes.alt} title={value.attributes.title} src={source} style={{display: 'block', maxWidth: '100%', maxHeight: parent === 'Explanation' ? '300px' : undefined, margin: '1em auto', backgroundColor: value.attributes.background || 'inherit', filter: 'invert(' + (value.attributes.inverted === 'true' ? .888 : 0) + ')'}} />;
			}

			if (value.name === 'caption') {
				return <span key={key} style={{display: 'block', fontSize: '0.7em', textAlign: 'center', marginTop: '-1em', color: '#cccccc'}}>{this.parse(value.elements, context)}</span>;
			}

			if (value.name === 'imgOld') {
				let cutTop = 0, cutRight = 0, cutBottom = 0, cutLeft = 0;

				if (value.attributes.crop) {
					const cuts = (value.attributes.crop as string).split(' ').map(crop => Number(crop));
					if (cuts.length === 1) {
						cutTop = cutRight = cutBottom = cutLeft = cuts[0];
					} else if (cuts.length === 2) {
						cutTop = cutBottom = cuts[0];
						cutRight = cutLeft = cuts[1];
					} else if (cuts.length === 2) {
						cutTop = cuts[0];
						cutRight = cutLeft = cuts[1];
						cutBottom = cuts[2];
					} else if (cuts.length === 4) {
						cutTop = cuts[0];
						cutRight = cuts[1];
						cutBottom = cuts[2];
						cutLeft = cuts[3];
					}
				}

				const translateX = cutRight / 2 - cutLeft / 2;
				const translateY = cutBottom - cutTop;

				// {/*<div style={{overflow: 'hidden'}}>*/}
				// 	{/*<div key={key} style={{display: 'inline-block', position: 'relative', transform: 'translate(' + translateX + '%, ' + translateY + '%)'}}>*/}
				// 		{/*<img alt={value.attributes.alt} title={value.attributes.title} src={value.attributes.src} style={{display: 'block', maxWidth: '100%', backgroundColor: value.attributes.background || 'inherit', filter: 'invert(' + (value.attributes.inverted === 'true' ? .888 : 0) + ')'}} />*/}
				// 		{/*{cutTop !== 0 ? <div style={{position: 'absolute', width: '100%', left: 0, top: 0, height: cutTop + '%', backgroundColor: '#1d1d1d'}} /> : null}*/}
				// 		{/*{cutRight !== 0 ? <div style={{position: 'absolute', width: cutRight + '%', top: 0, right: 0, height: '100%', backgroundColor: '#1d1d1d'}} /> : null}*/}
				// 		{/*{cutBottom !== 0 ? <div style={{position: 'absolute', width: '100%', bottom: 0, left: 0, height: cutBottom + '%', backgroundColor: '#1d1d1d'}} /> : null}*/}
				// 		{/*{cutLeft !== 0 ? <div style={{position: 'absolute', width: cutLeft + '%', top: 0, left: 0, height: '100%', backgroundColor: '#1d1d1d'}} /> : null}*/}
				// 	{/*</div>*/}
				// {/*</div>*/}
				return (
					<div style={{overflow: 'hidden'}}>
						<div key={key} style={{display: 'inline-block', position: 'relative', margin: '1em auto', transform: 'translateX(' + translateX + '%)'}}>
							<img alt={value.attributes.alt} title={value.attributes.title} src={value.attributes.src} style={{display: 'block', maxWidth: '100%', backgroundColor: value.attributes.background || 'inherit', filter: 'invert(' + (value.attributes.inverted === 'true' ? .888 : 0) + ')'}} />
							{cutTop !== 0 ? <div style={{position: 'absolute', width: '100%', left: 0, top: 0, height: cutTop + '%', backgroundColor: '#1d1d1d'}} /> : null}
							{cutRight !== 0 ? <div style={{position: 'absolute', width: cutRight + '%', top: 0, right: 0, height: '100%', backgroundColor: '#1d1d1d'}} /> : null}
							{cutBottom !== 0 ? <div style={{position: 'absolute', width: '100%', bottom: 0, left: 0, height: cutBottom + '%', backgroundColor: '#1d1d1d'}} /> : null}
							{cutLeft !== 0 ? <div style={{position: 'absolute', width: cutLeft + '%', top: 0, left: 0, height: '100%', backgroundColor: '#1d1d1d'}} /> : null}
						</div>
					</div>
				);
			}

			if (value.name === 'Image') {
				return this.imageElement(key, value);
			}

			if (value.name === 'br') {
				return <br key={key}/>;
			}

			if (value.name === 'MathBox') {
				return <MathBoxComponent key={key} value={value.elements[0].text} />;
			}

			if (value.name === 'subtext') {
				return <p style={{fontSize: '0.7em', textAlign: 'center', color: '#cccccc'}}>{this.parse(value.elements, context)}</p>;
			}

			if (value.name === 'Math') {
				let text = '';

				value.elements.forEach((element: any) => {
					if (element.text) {
						text += element.text;
					} else {
						text += this.parse(element, context);
					}
				});

				return <MathComponent key={key} value={text} />;
			}

			if (value.name === 'Math') {
				return <MathComponent key={key} value={value.elements[0].text} />;
			}

			if (value.name === 'LinesGL') { return <LinesComponent key={key} width={value.attributes.width} height={value.attributes.height} />; }

			return this.errorDiv(key, <span>Unknown element <strong>{value.name}</strong> encountered while parsing ExerciseComponent.</span>);
		}

		if (value === undefined) {
			return '';
		}

		return this.errorDiv(key, <span>Unknown value <strong>{value}</strong> encountered while parsing ExerciseComponent.</span>);
	}

	private imageElement (key: number | undefined, value: any) {
		const attributes = value.attributes;

		const attributeIndeces = ['name', 'author', 'via', 'license', 'link', 'source'];

		const undefinedIndex = attributeIndeces.findIndex(attributeIndex => attributes[attributeIndex] === undefined);

		if (undefinedIndex !== -1) {
			return this.errorDiv(key, <span>Image has no <strong>{attributeIndeces[undefinedIndex]}</strong> attribute.</span>);
		}

		const validLicenses = ['CC BY-SA 3.0', 'GFDL'];

		if (!validLicenses.some(license => license === attributes.license)) {
			return this.errorDiv(key, <span>Image must have one of the following licenses: {validLicenses.join(', ')}</span>);
		}

		const licenseLinkMap: StringMap<string> = {
			'CC BY-SA 3.0': 'http://creativecommons.org/licenses/by-sa/3.0',
			'GFDL': 'http://www.gnu.org/copyleft/fdl.html'
		};

		// previous width: value.attributes.width || '50%'

		return (
			<a key={key} title={attributes.name + ' - ' + ' by ' + attributes.author + (attributes.via !== '' ? ' - via ' + attributes.via : '') + (attributes.license !== '' ? ' - ' + attributes.license + ' (' + licenseLinkMap[attributes.license] + ')' : '')} href={attributes.link} target='_blank'>
				<img src={attributes.source} style={{display: 'block', maxWidth: '720px', maxHeight: '360px', margin: '1em auto', filter: 'invert(' + (value.attributes.inverted === 'true' ? .888 : 0) + ')'}} />
			</a>
		);
	}

	private errorDiv (key: number | undefined, message: string | JSX.Element) {
		return <div key={key} style={{color: 'red'}}>{message}</div>;
	}
}


// WEBPACK FOOTER //
// ./code/project/pages/components/exercises/ExerciseComponent.tsx